'use client'

import { useEffect, useState } from 'react'
import { useVTTStore } from '@/store'
import { socketService } from '@/services/socket'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'

/**
 * Compact selected-object HUD — a single floating grimoire card at the bottom
 * centre that drives whatever is currently selected: a standalone **light**
 * (radius + delete) or a **token** (torch, see-as, open sheet, delete).
 *
 * This is the mobile counterpart to the big bottom-left `CanvasOverlay` card.
 * On mobile that card is suppressed and this HUD owns both tokens and lights;
 * on desktop this isn't mounted (the desktop card handles both). Keep both
 * panels behaviourally in sync.
 *
 * Token mode is deliberately lean: the full identity / HP / stat sheet lives on
 * the desktop card and the in-app character sheet. Mobile surfaces only the
 * interactions you can't get elsewhere mid-combat — torch, see-as, sheet,
 * delete. Everything else is on the sheet or the right-click menu.
 */
const MIN_RADIUS = 0
const MAX_RADIUS = 60

interface PropertiesHUDProps {
  /** Viewer role. 'gm' unlocks light controls, delete and the see-as preview. */
  userRole?: string
  /** Viewer's user id — gates owner-only token actions (torch). */
  currentUserId?: string
  /** Delete the selected token. GM-only (server enforces); UI gated to match. */
  onDelete?: (tokenId: string) => void
  /** Owner-or-GM: toggle this token's torch / set light radius. */
  onLightChange?: (
    tokenId: string,
    updates: { lightRadius?: number | null; torchActive?: boolean },
  ) => void
}

export function PropertiesHUD({
  userRole,
  currentUserId,
  onDelete,
  onLightChange,
}: PropertiesHUDProps) {
  const {
    getActiveMapTokens,
    selectedTokenIds,
    openCharacterSheet,
    previewingVisionFor,
    setPreviewingVisionFor,
    selectedLightId,
    lightSources,
    updateLightSourceInStore,
    removeLightSource,
    setSelectedLightId,
  } = useVTTStore()

  const tokens = getActiveMapTokens()
  const selectedToken =
    selectedTokenIds.length === 1
      ? tokens.find((t) => t.id === selectedTokenIds[0]) ?? null
      : null
  const light = lightSources.find((ls) => ls.id === selectedLightId)

  // Two-step delete: first tap arms, second within 4s confirms. Reset whenever
  // the selection changes so a primed delete doesn't leak across objects.
  const [deleteArmed, setDeleteArmed] = useState(false)
  const selectionKey = selectedToken?.id ?? light?.id ?? null
  useEffect(() => {
    setDeleteArmed(false)
  }, [selectionKey])

  const isGM = userRole === 'gm'

  // ── Token mode (takes precedence — token + light shouldn't both be set) ──
  if (selectedToken) {
    const canEdit =
      isGM ||
      (selectedToken.ownerUserId != null &&
        selectedToken.ownerUserId === currentUserId)
    const hasVision =
      typeof selectedToken.visionRadius === 'number' &&
      selectedToken.visionRadius > 0
    const seeing = previewingVisionFor === selectedToken.id

    const armOrDelete = () => {
      if (!onDelete) return
      if (!deleteArmed) {
        setDeleteArmed(true)
        window.setTimeout(() => setDeleteArmed(false), 4000)
        return
      }
      onDelete(selectedToken.id)
    }

    return (
      <div className="ac-hud-card vtt-properties-hud">
        <span className="font-serif-ac vtt-properties-hud__title">
          {selectedToken.name}
        </span>

        {canEdit && onLightChange && (
          <IconButton
            size="sm"
            variant="ghost"
            className="!w-[32px] !h-[32px]"
            style={{
              color: selectedToken.torchActive
                ? 'hsl(var(--ac-text-light))'
                : undefined,
              background: selectedToken.torchActive
                ? 'hsl(var(--ac-gm-gold) / 0.7)'
                : undefined,
            }}
            title={selectedToken.torchActive ? 'Turn torch off' : 'Turn torch on'}
            aria-label={selectedToken.torchActive ? 'Turn torch off' : 'Turn torch on'}
            aria-pressed={!!selectedToken.torchActive}
            onClick={() =>
              onLightChange(selectedToken.id, {
                torchActive: !selectedToken.torchActive,
              })
            }
          >
            {/* flame glyph */}
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
            </svg>
          </IconButton>
        )}

        {isGM && hasVision && (
          <IconButton
            size="sm"
            variant="ghost"
            className="!w-[32px] !h-[32px]"
            style={{
              color: seeing ? 'hsl(var(--ac-text-light))' : undefined,
              background: seeing ? 'hsl(186 80% 40% / 0.7)' : undefined,
            }}
            title={seeing ? `Stop seeing as ${selectedToken.name}` : `See as ${selectedToken.name}`}
            aria-label={seeing ? 'Stop seeing as this token' : 'See as this token'}
            aria-pressed={seeing}
            onClick={() =>
              setPreviewingVisionFor(seeing ? null : selectedToken.id)
            }
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </IconButton>
        )}

        {selectedToken.characterId && (
          <IconButton
            size="sm"
            variant="ghost"
            className="!w-[32px] !h-[32px]"
            title="Open character sheet"
            aria-label="Open character sheet"
            onClick={() => openCharacterSheet(selectedToken.characterId as string)}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
          </IconButton>
        )}

        {isGM && onDelete && (
          <IconButton
            size="sm"
            variant="destructive"
            className="!w-[32px] !h-[32px]"
            style={{
              color: deleteArmed ? 'hsl(var(--ac-text-light))' : undefined,
              background: deleteArmed ? 'hsl(var(--ac-danger))' : undefined,
            }}
            title={deleteArmed ? `Tap again to delete ${selectedToken.name}` : `Delete ${selectedToken.name}`}
            aria-label={deleteArmed ? 'Confirm delete token' : 'Delete token'}
            onClick={armOrDelete}
          >
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </IconButton>
        )}
      </div>
    )
  }

  // ── Light mode (GM only) ─────────────────────────────────────────────────
  if (isGM && light) {
    const clamp = (n: number) =>
      Math.max(MIN_RADIUS, Math.min(MAX_RADIUS, Math.round(n)))
    const update = (updates: Partial<typeof light>) => {
      updateLightSourceInStore(light.id, updates)
      try {
        socketService.updateLightSource(light.id, updates)
      } catch {
        // socket down — optimistic value stands; session replay reconciles
      }
    }
    const bright = light.brightRadius ?? light.radius
    const dim = light.dimRadius ?? light.radius
    // Bright mirrors legacy radius; dim is clamped >= bright. Colour/falloff are
    // on the desktop card — mobile keeps the row tight with the two radii.
    const setBright = (n: number) => {
      const b = clamp(n)
      update({ brightRadius: b, radius: b, dimRadius: Math.max(dim, b) })
    }
    const setDim = (n: number) => update({ dimRadius: Math.max(clamp(n), bright) })
    const removeLight = () => {
      removeLightSource(light.id)
      setSelectedLightId(null)
      try {
        socketService.deleteLightSource(light.id)
      } catch {
        // socket down — broadcast on reconnect reconciles
      }
    }

    const num: React.CSSProperties = {
      minWidth: 16,
      textAlign: 'center',
      fontSize: '0.8125rem',
      color: 'hsl(var(--ac-text-light))',
    }
    const sun = (
      <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
      </svg>
    )
    const moon = (
      <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" />
      </svg>
    )
    return (
      <div className="ac-hud-card vtt-properties-hud">
        <span style={{ color: 'hsl(var(--ac-gm-gold))', display: 'inline-flex' }} title="Bright radius">{sun}</span>
        <IconButton size="sm" variant="ghost" className="!w-[28px] !h-[28px]" aria-label="Decrease bright radius" title="Decrease bright radius" onClick={() => setBright(bright - 1)}>
          −
        </IconButton>
        <span className="font-mono-ac" style={num}>{bright}</span>
        <IconButton size="sm" variant="ghost" className="!w-[28px] !h-[28px]" aria-label="Increase bright radius" title="Increase bright radius" onClick={() => setBright(bright + 1)}>
          +
        </IconButton>
        <span style={{ color: 'hsl(var(--ac-text-muted))', display: 'inline-flex' }} title="Dim radius">{moon}</span>
        <IconButton size="sm" variant="ghost" className="!w-[28px] !h-[28px]" aria-label="Decrease dim radius" title="Decrease dim radius" onClick={() => setDim(dim - 1)}>
          −
        </IconButton>
        <span className="font-mono-ac" style={num}>{dim}</span>
        <IconButton size="sm" variant="ghost" className="!w-[28px] !h-[28px]" aria-label="Increase dim radius" title="Increase dim radius" onClick={() => setDim(dim + 1)}>
          +
        </IconButton>
        <input
          type="color"
          value={light.color ?? '#ffffff'}
          onChange={(e) => update({ color: e.target.value })}
          title="Light color"
          aria-label="Light color"
          style={{
            width: 26,
            height: 26,
            padding: 0,
            border: '1px solid hsl(var(--ac-dark-accent))',
            borderRadius: 6,
            background: 'none',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        />
        <IconButton
          size="sm"
          variant="destructive"
          className="!w-[28px] !h-[28px]"
          title="Delete light"
          aria-label="Delete light"
          onClick={removeLight}
        >
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
          </svg>
        </IconButton>
      </div>
    )
  }

  return null
}
